import type {
  CitationGraph,
  CitationNode,
  ContentAsset,
  RelatedResource,
  RelatedResourceKind,
} from '../../../src/contracts/types.ts';
import { citationGraphEmptyState } from '../../../src/core/contentDisplay.ts';
import type { CategorySummary, FeedItem, GraphResponse, MeResponse, PaperDetailResponse } from './api.ts';
import { ApiError } from './api.ts';
import {
  fetchBillingStatus,
  fetchPaperRequestAvailability,
  submitPaperRequest,
  fetchCategories,
  fetchLegal,
  fetchFeed,
  fetchGraph,
  fetchMe,
  fetchPaper,
  deleteAccount,
  login,
  logout,
  pushSync,
  openBillingPortal,
  requestPasswordReset,
  resetPassword,
  search,
  signup,
  startCheckout,
  verifyEmail,
} from './api.ts';
import requestConfig from '../../../config/paper-requests.json' with { type: 'json' };
import { renderTextAsset, renderVerificationSummary } from './blocks.ts';
import { clear, el, formatDate, skeletonCards, stateMessage } from './dom.ts';
import type { GraphFilters } from './graph.ts';
import { renderCitationGraph } from './graph.ts';
import {
  clearHistory,
  getBookmarks,
  getBookmarksForSync,
  getHistory,
  getPreferences,
  isBookmarked,
  recordHistory,
  replaceBookmarks,
  replaceHistory,
  savePreferences,
  toggleBookmark,
} from './state.ts';

export type Navigate = (path: string, options?: { replace?: boolean }) => void;

let categoryCache: CategorySummary[] | null = null;

/** 関連資料の種類の表示名。URL の分類は取得側（原文からの決定的抽出）が決める。 */
const RELATED_RESOURCE_KIND_LABELS: Record<RelatedResourceKind, string> = {
  code: 'コード',
  data: 'データ',
  model: 'モデル',
  package: 'パッケージ',
  other: 'その他',
};

/** 原文から抜き出した URL は信頼できない入力。http(s) 以外（javascript: など）はリンクにしない。 */
function isLinkableUrl(url: string): boolean {
  return /^https?:\/\/[^\s]+$/i.test(url);
}

function renderRelatedResources(resources: RelatedResource[]): HTMLElement | null {
  const linkable = resources.filter((resource) => isLinkableUrl(resource.url));
  if (linkable.length === 0) return null;
  const section = el('div', { class: 'related-resources' });
  section.appendChild(el('h3', {}, '関連資料（原論文に記載のリンク）'));
  const list = el('ul', { class: 'related-resources__list' });
  for (const resource of linkable) {
    list.appendChild(
      el(
        'li',
        { class: 'related-resources__item' },
        el('span', { class: 'chip' }, RELATED_RESOURCE_KIND_LABELS[resource.kind] ?? RELATED_RESOURCE_KIND_LABELS.other),
        el(
          'a',
          { href: resource.url, target: '_blank', rel: 'noopener noreferrer' },
          resource.label || resource.url,
          el('span', { 'aria-hidden': 'true' }, ' ↗'),
        ),
        el('span', { class: 'related-resources__source' }, resource.source.section ? `原文（${resource.source.section}）に記載` : '原文に記載'),
      ),
    );
  }
  section.appendChild(list);
  // リンク先の中身は確認していない。生存・内容を保証しないことを書く（確認していないことを確認済みに見せない）。
  section.appendChild(
    el('p', { class: 'muted' }, '原論文の本文に書かれていた URL をそのまま示しています。リンク先の内容や公開状況は確認していません。外部サイトが開きます。'),
  );
  return section;
}

type ReferenceNode = CitationNode & { slug?: string };

/** 中心論文が直接参照した辺だけを、graph の順序を保って取り出す。深さ2の枝は参考文献一覧へ混ぜない。 */
export function directReferenceNodes(graph: CitationGraph): ReferenceNode[] {
  const nodes = new Map(graph.nodes.map((node) => [node.key, node as ReferenceNode]));
  const seen = new Set<string>();
  const references: ReferenceNode[] = [];
  for (const edge of graph.edges) {
    if (edge.direction !== 'references' || edge.fromKey !== graph.centerKey || seen.has(edge.toKey)) continue;
    const node = nodes.get(edge.toKey);
    if (!node) continue;
    seen.add(edge.toKey);
    references.push(node);
  }
  return references;
}

function mergeReferenceNodes(graph: CitationGraph | null, stored: CitationNode[]): ReferenceNode[] {
  const result: ReferenceNode[] = graph ? directReferenceNodes(graph) : [];
  const seen = new Set(result.map((node) => node.key));
  for (const node of stored) {
    if (seen.has(node.key)) continue;
    seen.add(node.key);
    result.push(node);
  }
  return result;
}

function externalPaperUrl(node: CitationNode): string | undefined {
  if (node.url && isLinkableUrl(node.url)) return node.url;
  const doi = node.identifiers.doi?.replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, '').replace(/^doi:/i, '');
  if (doi) return `https://doi.org/${doi}`;
  const arxivId = node.identifiers.arxivId?.replace(/^arxiv:/i, '');
  if (arxivId) return `https://arxiv.org/abs/${arxivId}`;
  if (node.identifiers.pmid) return `https://pubmed.ncbi.nlm.nih.gov/${node.identifiers.pmid}/`;
  return undefined;
}

function referenceActions(node: ReferenceNode): HTMLElement {
  const actions = el('div', { class: 'reference-card__actions' });
  const originalUrl = externalPaperUrl(node);
  if (originalUrl) {
    actions.appendChild(el('a', { href: originalUrl, target: '_blank', rel: 'noopener noreferrer' }, '原論文を開く ↗'));
  }
  // internal=true だけでは AI 解説の生成完了を保証しない。公開 Worker が解決した slug がある場合だけ収載ページへ張る。
  if (node.slug) actions.appendChild(el('a', { href: `/papers/${node.slug}`, 'data-link': '' }, 'PaperStock収載ページ →'));
  if (actions.childElementCount === 0) actions.appendChild(el('span', { class: 'muted' }, 'リンク未確認'));
  return actions;
}

function referenceDetails(node: ReferenceNode): string {
  return [node.authors.slice(0, 4).join(', '), node.venue].filter(Boolean).join(' · ');
}

function renderReferences(args: {
  nodes: ReferenceNode[];
  status: 'complete' | 'partial' | 'stored' | 'unknown';
}): HTMLElement {
  const section = el('section', { class: 'reference-section', 'aria-labelledby': 'references-title', id: 'references' });
  section.append(
    el('p', { class: 'reference-section__eyebrow' }, '研究のつながり'),
    el('h2', { id: 'references-title' }, 'この論文が参照した研究'),
  );
  const scope = args.status === 'partial'
    ? '提供元から取得できた範囲を表示しています。全件ではない可能性があります。'
    : args.status === 'stored'
      ? '論文データに保存されている範囲を表示しています。提供元の取得範囲は確認できていません。'
      : args.status === 'unknown'
        ? '参考文献情報を今回は取得できませんでした。参考文献がないという意味ではありません。'
        : '提供元から取得できた直接の参考文献を表示しています。';
  section.appendChild(el('p', { class: 'reference-section__intro' }, scope));

  if (args.nodes.length === 0) {
    section.appendChild(
      el(
        'p',
        { class: 'reference-section__empty' },
        args.status === 'complete'
          ? '提供元から取得した範囲では、直接の参考文献を確認できませんでした。'
          : '表示できる参考文献情報はありません。未取得の資料がある可能性があります。',
      ),
    );
    return section;
  }

  const list = el('ol', { class: 'reference-list' });
  args.nodes.slice(0, 3).forEach((node, index) => {
    const card = el('article', { class: 'reference-card' });
    card.appendChild(el('p', { class: 'reference-card__index' }, `参考文献 ${String(index + 1).padStart(2, '0')}${node.year ? ` · ${node.year}` : ''}`));
    card.appendChild(el('h3', {}, node.title));
    const details = referenceDetails(node);
    if (details) card.appendChild(el('p', { class: 'reference-card__meta' }, details));
    card.appendChild(referenceActions(node));
    list.appendChild(el('li', {}, card));
  });
  section.appendChild(list);
  if (args.nodes.length > 3) {
    const all = el('details', { class: 'all-references' });
    all.appendChild(el('summary', {}, `残りの参考文献を見る（${args.nodes.length - 3}件）`));
    const compact = el('ol', { start: '4' });
    args.nodes.slice(3).forEach((node) => {
      const item = el('li', {});
      item.appendChild(el('h3', {}, `${node.title}${node.year ? `（${node.year}）` : ''}`));
      const details = referenceDetails(node);
      if (details) item.appendChild(el('p', {}, details));
      item.appendChild(referenceActions(node));
      compact.appendChild(item);
    });
    all.appendChild(compact);
    section.appendChild(all);
  }
  return section;
}

async function loadCategories(): Promise<CategorySummary[]> {
  if (categoryCache) return categoryCache;
  const response = await fetchCategories();
  categoryCache = response.categories;
  return categoryCache;
}

function segmented<T extends string>(
  label: string,
  items: Array<{ value: T; label: string; disabled?: boolean; title?: string }>,
  current: T,
  onChange: (value: T) => void,
): HTMLElement {
  const group = el('div', { class: 'segmented', role: 'group', 'aria-label': label });
  for (const item of items) {
    group.appendChild(
      el(
        'button',
        {
          type: 'button',
          class: 'segmented__item',
          'aria-pressed': String(item.value === current),
          disabled: item.disabled,
          title: item.title,
          onclick: () => {
            if (!item.disabled) onChange(item.value);
          },
        },
        item.label,
      ),
    );
  }
  return group;
}

function integrityChip(flag: FeedItem['integrityFlag']): HTMLElement | null {
  switch (flag) {
    case 'retracted':
      return el('span', { class: 'chip chip--danger' }, '撤回された論文');
    case 'withdrawn':
      return el('span', { class: 'chip chip--danger' }, '取り下げられた論文');
    case 'corrected':
      return el('span', { class: 'chip chip--warn' }, '訂正あり');
    case 'unchecked':
      return el('span', { class: 'chip' }, '撤回・訂正は未確認');
    default:
      return null;
  }
}

function paperCard(item: FeedItem): HTMLElement {
  const card = el('a', {
    class: 'paper-card',
    href: `/papers/${item.slug}`,
    'data-link': '',
    style: `--card-accent:${item.categoryAccent || 'var(--accent)'}`,
  });

  card.appendChild(
    el(
      'div',
      { class: 'paper-card__meta' },
      el('span', { class: 'chip chip--accent' }, item.categoryName),
      el('span', {}, formatDate(item.runDate)),
      item.year ? el('span', {}, `${item.year}年の論文`) : null,
      integrityChip(item.integrityFlag),
      item.textSourceScope === 'abstract_only' ? el('span', { class: 'chip chip--warn' }, '要旨のみ') : null,
    ),
  );

  const cardHeadline = item.headline || item.titleJa || item.title;
  card.appendChild(el('p', { class: 'paper-card__headline' }, cardHeadline));
  // 一覧でも、日本語の題名があるならそれを出す。原題は詳細画面に必ず出る。
  if (cardHeadline !== item.title) {
    card.appendChild(el('p', { class: 'paper-card__title' }, item.titleJa || item.title));
  }
  else card.appendChild(el('p', { class: 'paper-card__title' }, '解説は準備中です'));

  if (item.selectionReasons.length > 0) {
    card.appendChild(el('p', { class: 'paper-card__reason' }, `選定理由：${item.selectionReasons[0]}`));
  }

  const available = item.availableAssets.some((asset) => asset.level === 'beginner' && asset.medium === 'text');
  card.appendChild(el('div', { class: 'paper-card__assets' }, el('span', { class: 'chip' }, available ? '解説あり' : '準備中')));

  return card;
}

function featuredPaperCard(item: FeedItem): HTMLElement {
  const card = el('article', {
    class: 'featured-paper',
    style: `--card-accent:${item.categoryAccent || 'var(--accent)'}`,
  });
  const content = el('div', { class: 'featured-paper__content' });
  content.appendChild(el('span', { class: 'featured-paper__eyebrow' }, 'PAPER OF THE DAY'));
  content.appendChild(
    el(
      'div',
      { class: 'paper-card__meta' },
      el('span', { class: 'chip chip--accent' }, item.categoryName),
      el('span', {}, formatDate(item.runDate)),
      item.year ? el('span', {}, `${item.year}年の論文`) : null,
      integrityChip(item.integrityFlag),
      item.textSourceScope === 'abstract_only' ? el('span', { class: 'chip chip--warn' }, '要旨のみ') : null,
    ),
  );
  const headline = item.headline || item.titleJa || item.title;
  content.appendChild(
    el(
      'h2',
      { class: 'featured-paper__headline' },
      el('a', { href: `/papers/${item.slug}`, 'data-link': '' }, headline),
    ),
  );
  if (headline !== item.title) content.appendChild(el('p', { class: 'featured-paper__title' }, item.titleJa || item.title));
  if (item.selectionReasons[0]) {
    content.appendChild(el('p', { class: 'featured-paper__reason' }, item.selectionReasons[0]));
  }
  content.appendChild(
    el('a', { class: 'featured-paper__cta', href: `/papers/${item.slug}`, 'data-link': '' }, '研究の要点と解説を読む', el('span', { 'aria-hidden': 'true' }, '→')),
  );
  card.appendChild(content);
  return card;
}

// --------------------------------------------------------------- 初回設定

/**
 * 初期設定のあとに戻る先。共有された論文リンクを初めて開いた人が、初期設定のあと最新一覧ではなく
 * その論文へ戻れるようにする（実測 2026-09-25: 初回は /papers/... が /onboarding へ置き換わり、元の URL が失われていた）。
 * 同じサイト内のパスだけを許し、外部 URL（//host や https:）や初期設定そのものへは戻さない。
 */
export function onboardingReturnPath(next: string | null | undefined): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || /[\\\s]/.test(next)) return '/';
  if (next === '/onboarding' || next.startsWith('/onboarding?') || next.startsWith('/onboarding/')) return '/';
  return next;
}

export async function renderOnboarding(root: HTMLElement, navigate: Navigate, params?: URLSearchParams): Promise<void> {
  clear(root);
  const returnPath = onboardingReturnPath(params?.get('next'));
  const preferences = getPreferences();
  const selected = new Set(preferences.categories);

  const container = el('div', { class: 'onboarding stack' });
  container.appendChild(
    el(
      'div',
      {},
      el('h1', { class: 'feed-head__title' }, 'PaperStock へようこそ'),
      el(
        'p',
        { class: 'muted' },
        'オープンアクセス論文を、図解・用語・原図の読み方・実践例まで一続きで解説します。解説は AI が原論文をもとに作成し、自動検証します。まず興味のある分野を選んでください。',
      ),
    ),
  );

  const categoriesCard = el('section', { class: 'card stack' });
  categoriesCard.appendChild(el('h2', { class: 'section-title' }, '興味のある分野', el('span', { class: 'section-title__note' }, '選ばなければ全分野を表示します')));
  const choiceGrid = el('div', { class: 'choice-grid' });
  categoriesCard.appendChild(choiceGrid);
  container.appendChild(categoriesCard);

  const actions = el('div', { class: 'row' });
  actions.appendChild(
    el(
      'button',
      {
        class: 'button button--primary',
        type: 'button',
        onclick: () => {
          savePreferences({ onboarded: true, categories: Array.from(selected) });
          navigate(returnPath, { replace: true });
        },
      },
      'はじめる',
    ),
  );
  actions.appendChild(
    el(
      'button',
      {
        class: 'button',
        type: 'button',
        onclick: () => {
          savePreferences({ onboarded: true });
          navigate(returnPath, { replace: true });
        },
      },
      'あとで設定する',
    ),
  );
  container.appendChild(actions);
  root.appendChild(container);

  async function renderOnboardingRefresh(): Promise<void> {
    const categories = await loadCategories();
    clear(choiceGrid);
    for (const category of categories) {
      const active = selected.has(category.code);
      choiceGrid.appendChild(
        el(
          'button',
          {
            type: 'button',
            class: 'choice',
            'aria-pressed': String(active),
            onclick: () => {
              if (selected.has(category.code)) selected.delete(category.code);
              else selected.add(category.code);
              void renderOnboardingRefresh();
            },
          },
          category.name,
        ),
      );
    }
  }

  await renderOnboardingRefresh();
}

// --------------------------------------------------------------- フィード

export async function renderFeed(root: HTMLElement, navigate: Navigate, params: URLSearchParams): Promise<void> {
  clear(root);
  const preferences = getPreferences();
  const activeCategory = params.get('category') ?? '';

  const head = el('div', { class: 'feed-head' });
  head.appendChild(
    el(
      'div',
      {},
      el('span', { class: 'feed-head__eyebrow' }, 'DAILY RESEARCH BRIEF'),
      el('h1', { class: 'feed-head__title' }, '知識の最前線を、', el('span', {}, '30秒で。')),
      el('p', { class: 'feed-head__lead' }, '20分野から選んだ論文を、本質・根拠・使いどころまで一続きで読み解きます。'),
    ),
  );
  head.appendChild(el('a', { class: 'button', href: '/requests', 'data-link': true }, '論文の解説をリクエスト'));
  root.appendChild(head);

  const scroller = el('div', { class: 'category-scroller' });
  root.appendChild(scroller);

  const list = el('div', {});
  list.appendChild(skeletonCards(6));
  root.appendChild(list);

  try {
    const categories = await loadCategories();
    clear(scroller);
    scroller.appendChild(
      el(
        'button',
        {
          class: `chip${activeCategory === '' ? ' chip--accent' : ''}`,
          type: 'button',
          onclick: () => navigate('/'),
        },
        'すべて',
      ),
    );
    const ordered = preferences.categories.length
      ? [...categories].sort(
          (a, b) =>
            Number(preferences.categories.includes(b.code)) - Number(preferences.categories.includes(a.code)),
        )
      : categories;
    for (const category of ordered) {
      scroller.appendChild(
        el(
          'button',
          {
            class: `chip${activeCategory === category.code ? ' chip--accent' : ''}`,
            type: 'button',
            onclick: () => navigate(`/?category=${category.code}`),
          },
          `${category.name}${category.paper_count ? `（${category.paper_count}）` : ''}`,
        ),
      );
    }

    const response = await fetchFeed({
      ...(activeCategory ? { category: activeCategory } : {}),
      limit: 30,
    });

    clear(list);
    if (response.items.length === 0) {
      list.appendChild(
        stateMessage(
          '🗒️',
          'まだ公開された論文がありません',
          '日次処理を実行し、内容を確認して公開すると、ここに表示されます。（scripts/paperstock run → publish）',
        ),
      );
      return;
    }

    const filtered =
      activeCategory === '' && preferences.categories.length > 0
        ? response.items.filter((item) => preferences.categories.includes(item.categoryCode))
        : response.items;
    const items = filtered.length > 0 ? filtered : response.items;
    const featured = items.find((item) => item.visualSummary?.length === 5);
    if (featured) list.appendChild(featuredPaperCard(featured));

    const remaining = featured ? items.filter((item) => item.paperId !== featured.paperId) : items;
    const grid = el('div', { class: 'paper-grid' });
    if (remaining.length > 0) {
      list.appendChild(
        el(
          'div',
          { class: 'feed-section-head' },
          el('span', { class: 'feed-head__eyebrow' }, 'EXPLORE BY FIELD'),
          el('h2', {}, '分野を横断して読む'),
        ),
      );
      for (const item of remaining) grid.appendChild(paperCard(item));
      list.appendChild(grid);
    }

    if (filtered.length === 0 && preferences.categories.length > 0) {
      list.insertBefore(
        el('p', { class: 'alert alert--info' }, '選んだ分野の論文がまだないため、全分野を表示しています。'),
        list.firstChild,
      );
    }
  } catch (error) {
    clear(list);
    list.appendChild(
      stateMessage(
        '⚠️',
        '読み込みに失敗しました',
        error instanceof Error ? error.message : String(error),
        el('button', { class: 'button', type: 'button', onclick: () => void renderFeed(root, navigate, params) }, '再試行'),
      ),
    );
  }
}

// --------------------------------------------------------------- 分野一覧

export async function renderCategories(root: HTMLElement, navigate: Navigate): Promise<void> {
  clear(root);
  root.appendChild(el('h1', { class: 'feed-head__title' }, '分野'));
  root.appendChild(
    el('p', { class: 'muted', style: 'margin-bottom:1.5rem' }, '分野の定義は Notion 側で追加・変更できます。'),
  );
  const grid = el('div', { class: 'category-grid' });
  root.appendChild(grid);

  try {
    const categories = await loadCategories();
    for (const category of categories) {
      grid.appendChild(
        el(
          'a',
          {
            class: 'category-tile',
            href: `/?category=${category.code}`,
            'data-link': '',
            style: `--tile-accent:${category.accent || 'var(--accent)'}`,
          },
          el('div', { class: 'category-tile__name' }, category.name),
          el('p', { class: 'muted' }, category.summary ?? ''),
          el('p', { class: 'muted' }, `収載 ${category.paper_count ?? 0} 本`),
        ),
      );
    }
    if (categories.length === 0) {
      clear(grid);
      grid.appendChild(stateMessage('🗂️', '分野がまだ登録されていません', 'scripts/paperstock publish を実行してください。'));
    }
  } catch (error) {
    clear(grid);
    grid.appendChild(stateMessage('⚠️', '読み込みに失敗しました', error instanceof Error ? error.message : String(error)));
  }
  void navigate;
}

// --------------------------------------------------------------- 論文詳細

export async function renderPaper(root: HTMLElement, navigate: Navigate, slug: string): Promise<void> {
  clear(root);
  const loading = el(
    'div',
    { class: 'paper stack' },
    el('div', { class: 'skeleton', style: 'height:1.2rem;width:35%' }),
    el('div', { class: 'skeleton', style: 'height:2.4rem;width:90%' }),
    el('div', { class: 'skeleton', style: 'height:9rem;width:100%' }),
  );
  root.appendChild(loading);

  let detail: PaperDetailResponse;
  try {
    detail = await fetchPaper(slug);
  } catch (error) {
    clear(root);
    root.appendChild(
      stateMessage(
        '🔍',
        '論文が見つかりません',
        error instanceof Error ? error.message : String(error),
        el('button', { class: 'button', type: 'button', onclick: () => navigate('/') }, '最新の論文へ'),
      ),
    );
    return;
  }

  clear(root);
  const { paper, category, assets, graph } = detail;
  const preferences = getPreferences();

  recordHistory({
    paperId: paper.id,
    slug: paper.slug,
    title: paper.title,
    headline: paper.headline,
    categoryName: category?.name ?? paper.categoryCode,
  });

  const container = el('article', { class: 'paper' });

  // --- 見出し ---
  const eyebrow = el('div', { class: 'paper__eyebrow' });
  if (category) eyebrow.appendChild(el('span', { class: 'chip chip--accent' }, category.name));
  eyebrow.appendChild(el('span', { class: 'chip' }, formatDate(paper.runDate)));
  if (paper.selection.modeLabel) eyebrow.appendChild(el('span', { class: 'chip' }, paper.selection.modeLabel));
  eyebrow.appendChild(el('span', { class: 'chip' }, `参照範囲: ${paper.textSourceScope === 'full_text' ? '全文' : paper.textSourceScope === 'abstract_only' ? '要旨' : '本文未取得'}`));
  container.appendChild(eyebrow);

  const pageHeadline = paper.headline || paper.titleJa || paper.title;
  container.appendChild(el('p', { class: 'paper__headline-label' }, 'この論文の中心主張'));
  container.appendChild(el('h1', { class: 'paper__title' }, pageHeadline));
  // **日本語の題名は原題と並べて出す。**
  // 見出し（headline）がある論文では `headline || titleJa || title` の順で
  // titleJa まで到達せず、生成して検証まで通した題名が画面に一度も出なかった。
  // 原題は必ず残す（置き換えない）。
  if (paper.titleJa && paper.titleJa !== pageHeadline) {
    container.appendChild(el('p', { class: 'paper__title-ja' }, paper.titleJa));
  }
  if (pageHeadline !== paper.title) {
    container.appendChild(el('p', { class: 'paper__original-title' }, paper.title));
  }
  container.appendChild(
    el(
      'p',
      { class: 'paper__authors' },
      [
        paper.authors.map((a) => a.name).slice(0, 8).join(', ') + (paper.authors.length > 8 ? ' ほか' : ''),
        paper.venue,
        paper.publishedDate ? formatDate(paper.publishedDate) : null,
      ]
        .filter(Boolean)
        .join('｜'),
    ),
  );

  // --- 撤回・訂正の警告 ---
  if (paper.integrity.retracted || paper.integrity.withdrawn) {
    container.appendChild(
      el(
        'div',
        { class: 'alert alert--danger', role: 'alert' },
        paper.integrity.retracted ? 'この論文は撤回されています。' : 'この論文は取り下げられています。',
        ' 内容を有効な知見として扱わないでください。',
      ),
    );
  } else if (paper.integrity.corrections.length > 0) {
    container.appendChild(
      el(
        'div',
        { class: 'alert alert--warn', role: 'alert' },
        `この論文には訂正情報があります（${paper.integrity.corrections.map((c) => c.type).join(', ')}）。`,
      ),
    );
  }
  // 読み方は1つだけ。選択UIを置かず、理解に必要な本文へ直行する。
  const bookmarkButton = el('button', { class: 'button', type: 'button' });
  const syncBookmarkLabel = (): void => {
    const marked = isBookmarked(paper.id);
    bookmarkButton.textContent = marked ? '★ ブックマーク済み' : '☆ ブックマーク';
    bookmarkButton.setAttribute('aria-pressed', String(marked));
  };
  bookmarkButton.addEventListener('click', () => {
    toggleBookmark({
      paperId: paper.id,
      slug: paper.slug,
      title: paper.title,
      headline: paper.headline,
      categoryName: category?.name ?? paper.categoryCode,
    });
    syncBookmarkLabel();
  });
  syncBookmarkLabel();
  const actions = el('div', { class: 'paper__actions' }, bookmarkButton);
  // 一次ボタンは urls.html（原論文 HTML）→ urls.pdf の順。
  // landing が arxiv.org/html/ のときに landing を HTML と見なす分岐は、D1 に html 列が無かった時期の移行互換。
  // Worker が html_url 列と urls.html の復元を終えたら削除する予定（ホスト名で判定するのは正本ではない）。
  const fullHtmlUrl = paper.urls.html
    ?? (paper.urls.landing?.startsWith('https://arxiv.org/html/') ? paper.urls.landing : undefined);
  const fullTextUrl = fullHtmlUrl ?? paper.urls.pdf;
  const canonicalPaperUrl = paper.urls.landing && paper.urls.landing !== fullTextUrl
    ? paper.urls.landing
    : fullHtmlUrl && paper.identifiers.arxivId
      ? `https://arxiv.org/abs/${paper.identifiers.arxivId}`
      : undefined;
  if (fullTextUrl) {
    actions.appendChild(
      el(
        'a',
        { class: 'button button--primary', href: fullTextUrl, target: '_blank', rel: 'noopener noreferrer' },
        fullHtmlUrl ? '原論文HTMLを読む ↗' : '原論文PDFを読む ↗',
      ),
    );
  }
  if (canonicalPaperUrl) {
    actions.appendChild(
      el('a', { class: 'button', href: canonicalPaperUrl, target: '_blank', rel: 'noopener noreferrer' }, '論文ページを開く ↗'),
    );
  }
  container.appendChild(actions);

  const body = el('div', { class: 'paper__body' });
  const asset: ContentAsset | undefined = assets.find((item) => item.level === 'beginner' && item.medium === 'text');
  if (!asset) {
    body.appendChild(stateMessage('🧩', '解説はまだありません', '生成と自動検証が完了すると、ここに表示されます。'));
  } else if (paper.textSourceScope !== 'full_text') {
    // Worker も full_text 以外の解説を配信から外すが、画面側でも重ねて防ぐ（二重の防御）。
    // 要旨だけから作った解説や、公開後の再検査で取り下げ待ちのデータがキャッシュに残っていても、
    // 「全文確認済み」の体裁では見せない（AGENTS.md「要旨しか取得できていない論文を全文確認済みとして扱わない」）。
    // 有料の案内より先に置く。見せない解説のために契約を勧めない。
    body.appendChild(
      stateMessage(
        '📄',
        '全文を確認できていないため解説を表示していません',
        '原論文の全文を取得できた論文だけ解説を表示します。原論文は上のボタンから確認できます。',
      ),
    );
  } else if ((asset as ContentAsset & { locked?: boolean }).locked === true) {
    body.appendChild(stateMessage('🔒', 'この解説は現在利用できません', 'アカウント画面で利用状態をご確認ください。'));
  } else {
    const verified = asset.verification?.passed === true;
    body.appendChild(
      el(
        'div',
        { class: 'alert alert--info' },
        verified
          ? 'この解説は AI が原論文をもとに作成し、自動検証を通したものです。人が1件ずつ確認する工程はありません。正確なところは原論文をご確認ください。'
          : 'この解説は AI が原論文をもとに作成したものです。自動検証はまだ通っていません。人が1件ずつ確認する工程はありません。正確なところは原論文をご確認ください。',
      ),
    );
    body.appendChild(renderTextAsset(asset, paper));
  }
  container.appendChild(body);

  // --- 参考文献 ---
  // Paper API は配信サイズと閲覧境界の都合で references を落とす版がある。その場合は
  // 既存の graph API から中心論文の直接 references だけを復元し、取得範囲も同時に示す。
  const referencesHost = el('div', { class: 'references-host' });
  referencesHost.appendChild(el('div', { class: 'skeleton', style: 'height:10rem;border-radius:14px' }));
  container.appendChild(referencesHost);
  const loadReferences = async (): Promise<void> => {
    const stored = paper.references ?? [];
    try {
      // 詳細応答内の graph は保存時点の形で、配信中 PaperStock ページの slug を持たない。
      // graph endpoint は公開中の論文だけを照合して slug を補うため、リンク解決のためにもここでは照会する。
      const response: GraphResponse = await fetchGraph(paper.slug, { direction: 'references', internalOnly: false });
      clear(referencesHost);
      if (response.graph) {
        const partial = response.graph.truncated.references === true;
        referencesHost.appendChild(renderReferences({
          nodes: mergeReferenceNodes(response.graph, stored),
          status: partial ? 'partial' : 'complete',
        }));
        return;
      }
      if (graph) {
        referencesHost.appendChild(renderReferences({
          nodes: mergeReferenceNodes(graph, stored),
          status: graph.truncated.references ? 'partial' : 'complete',
        }));
        return;
      }
      const referencesIncomplete = response.truncated?.references === true || response.reason !== 'no_edges';
      referencesHost.appendChild(renderReferences({
        nodes: mergeReferenceNodes(null, stored),
        status: stored.length > 0 ? 'stored' : referencesIncomplete ? 'unknown' : 'complete',
      }));
    } catch (error) {
      clear(referencesHost);
      referencesHost.appendChild(renderReferences({
        nodes: mergeReferenceNodes(graph, stored),
        status: graph
          ? graph.truncated.references ? 'partial' : 'complete'
          : stored.length > 0 ? 'stored' : 'unknown',
      }));
      if (!(error instanceof ApiError && error.status === 402)) {
        referencesHost.appendChild(
          el('p', { class: 'muted reference-section__error' }, '参考文献データの再取得に失敗しました。時間を置くと表示できる場合があります。'),
        );
      }
    }
  };
  void loadReferences();

  const evidence = el('details', { class: 'paper-evidence' });
  evidence.appendChild(el('summary', {}, '出典と品質を確認'));
  if (paper.significance) evidence.appendChild(el('p', {}, paper.significance));
  evidence.appendChild(
    el(
      'div',
      { class: 'row' },
      el('span', { class: 'chip chip--ok' }, `ライセンス: ${paper.license.label}`),
      el('span', { class: 'chip' }, `本文: ${paper.textSourceScope === 'full_text' ? '全文' : paper.textSourceScope === 'abstract_only' ? '要旨のみ' : '未取得'}`),
      el('span', { class: 'chip' }, '作成: AI'),
      paper.citationCount !== undefined ? el('span', { class: 'chip' }, `引用数 ${paper.citationCount}`) : null,
    ),
  );
  if (paper.selection.reasons.length > 0) {
    evidence.appendChild(el('h3', {}, '選定理由'));
    evidence.appendChild(el('ul', { class: 'reason-list' }, ...paper.selection.reasons.map((reason) => el('li', {}, reason))));
  }
  evidence.appendChild(
    el(
      'div',
      { class: 'row' },
      paper.urls.pdf
        ? el('a', { class: 'chip', href: paper.urls.pdf, target: '_blank', rel: 'noopener noreferrer' }, '原論文PDF')
        : null,
      fullHtmlUrl
        ? el('a', { class: 'chip', href: fullHtmlUrl, target: '_blank', rel: 'noopener noreferrer' }, '原論文HTML')
        : null,
      canonicalPaperUrl
        ? el('a', { class: 'chip', href: canonicalPaperUrl, target: '_blank', rel: 'noopener noreferrer' }, '出版社・リポジトリの論文ページ')
        : null,
      paper.identifiers.doi
        ? el('a', { class: 'chip', href: `https://doi.org/${paper.identifiers.doi}`, target: '_blank', rel: 'noopener noreferrer' }, `DOI: ${paper.identifiers.doi}`)
        : null,
      paper.identifiers.arxivId ? el('span', { class: 'chip' }, `arXiv: ${paper.identifiers.arxivId}`) : null,
    ),
  );
  // 関連資料は一次ボタンの列には出さない（原論文そのものと、原論文が参照する外部リンクを混ぜない）。
  const relatedResources = renderRelatedResources(paper.relatedResources ?? []);
  if (relatedResources) evidence.appendChild(relatedResources);
  if (asset) {
    const verification = renderVerificationSummary(asset);
    if (verification) evidence.appendChild(verification);
  }
  container.appendChild(evidence);

  // --- 引用グラフ ---
  container.appendChild(
    el('h2', { class: 'heading-block' }, '引用関係を地図で読む'),
  );
  container.appendChild(
    el('p', { class: 'muted' }, '参考にした研究と、この研究を参考にした後続研究を、この論文を中心に並べます。'),
  );
  const graphHost = el('div', {});
  container.appendChild(graphHost);

  // 引用データの有無を見出しのチップにも出す。本文（全文）の有無とは別の情報だと分かるようにする。
  const citationChip = el('span', { class: 'chip' });
  let citationChipAttached = false;
  const setCitationChip = (text: string): void => {
    citationChip.textContent = text;
    if (!citationChipAttached) {
      eyebrow.appendChild(citationChip);
      citationChipAttached = true;
    }
  };

  let filters: GraphFilters = { direction: preferences.graphDirection, internalOnly: false };
  const drawGraph = async (): Promise<void> => {
    clear(graphHost);
    graphHost.appendChild(el('div', { class: 'skeleton', style: 'height:20rem;border-radius:16px' }));
    const unfiltered = filters.direction === 'all' && !filters.internalOnly;
    try {
      const response: GraphResponse =
        unfiltered && graph
          ? { graph }
          : await fetchGraph(paper.slug, { direction: filters.direction, internalOnly: filters.internalOnly });
      clear(graphHost);
      if (!response.graph) {
        // 本文もチップも同じ純関数から出す。別々に決めると「なし」と「断定できません」が並ぶ。
        const empty = citationGraphEmptyState({
          reason: response.reason,
          truncated: response.truncated,
          unfiltered,
        });
        graphHost.appendChild(stateMessage('🔗', empty.title, empty.detail));
        // graph が null になる理由は絞り込みより前に決まる（Worker は絞り込み前に null を返す）。
        // 保存済みの向き設定で最初から絞り込まれている場合でも、初回はチップを出す。
        if (unfiltered || !citationChipAttached) setCitationChip(empty.chip);
        return;
      }
      const partial = response.graph.truncated.references || response.graph.truncated.citedBy;
      // 絞り込み中の件数で上書きしない。まだ出していないときだけ、絞り込んだ件数を仮に出す。
      if (unfiltered || !citationChipAttached) {
        setCitationChip(`引用データ: ${response.graph.edges.length} 件${partial ? '以上' : ''}`);
      }
      graphHost.appendChild(
        renderCitationGraph(response.graph, {
          filters,
          containerWidth: graphHost.clientWidth || undefined,
          onSelect: (node) => {
            if (node.paperId) navigate(`/papers/${(node as { slug?: string }).slug ?? node.paperId}`);
          },
          onFilterChange: (next) => {
            filters = next;
            savePreferences({ graphDirection: next.direction });
            void drawGraph();
          },
        }),
      );
    } catch (error) {
      clear(graphHost);
      // 402 は不具合ではなく契約の境界。「失敗」と出すと有料機能が壊れて見える。
      if (error instanceof ApiError && error.status === 402) {
        const lock = el('div', { class: 'locked' });
        lock.appendChild(el('h3', { class: 'locked__title' }, '引用グラフは有料プランでご覧いただけます'));
        lock.appendChild(
          el(
            'p',
            { class: 'locked__body' },
            'この論文が何を引用し、どの論文から引用されたかをたどれます。月額500円（税込）のスタンダードプランの範囲です。',
          ),
        );
        const go = el('button', { class: 'button button--primary', type: 'button' }, 'プランを見る');
        go.addEventListener('click', () => navigate('/account'));
        lock.appendChild(go);
        graphHost.appendChild(lock);
        return;
      }
      graphHost.appendChild(
        stateMessage('⚠️', '引用グラフの読み込みに失敗しました', error instanceof Error ? error.message : String(error)),
      );
    }
  };
  void drawGraph();

  root.appendChild(container);
}

// --------------------------------------------------------------- 検索

export async function renderSearch(root: HTMLElement, navigate: Navigate, params: URLSearchParams): Promise<void> {
  clear(root);
  const initialQuery = params.get('q') ?? '';

  root.appendChild(el('h1', { class: 'feed-head__title' }, '検索'));
  const form = el('form', { class: 'row', style: 'margin:1rem 0 1.5rem', role: 'search' });
  const input = el('input', {
    class: 'field',
    type: 'search',
    name: 'q',
    value: initialQuery,
    placeholder: 'タイトル・著者・キーワード',
    'aria-label': '検索語',
    style: 'max-width:28rem',
  });
  form.appendChild(input);
  form.appendChild(el('button', { class: 'button button--primary', type: 'submit' }, '検索'));
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    navigate(`/search?q=${encodeURIComponent(input.value)}`);
  });
  root.appendChild(form);

  const results = el('div', {});
  root.appendChild(results);

  if (initialQuery.trim().length === 0) {
    results.appendChild(stateMessage('🔍', '検索語を入力してください', 'タイトル・日本語タイトル・要旨・見出し・著者名から探します。'));
    return;
  }

  results.appendChild(skeletonCards(3));
  try {
    const response = await search(initialQuery);
    clear(results);
    if (response.items.length === 0) {
      results.appendChild(stateMessage('🫙', '該当する論文がありません', '別の語で試すか、分野一覧から探してみてください。'));
      return;
    }
    results.appendChild(el('p', { class: 'muted' }, `${response.items.length} 件`));
    for (const item of response.items) {
      const headline = item.headline || item.titleJa || item.title;
      results.appendChild(
        el(
          'a',
          { class: 'list-item', href: `/papers/${item.slug}`, 'data-link': '' },
          el(
            'div',
            { class: 'list-item__body' },
            el('div', { class: 'list-item__title' }, headline),
            el('p', { class: 'muted' }, headline === item.title ? '解説は準備中です' : item.title),
            el(
              'div',
              { class: 'row' },
              el('span', { class: 'chip chip--accent' }, item.categoryName),
              el('span', { class: 'chip' }, formatDate(item.runDate)),
            ),
          ),
        ),
      );
    }
  } catch (error) {
    clear(results);
    results.appendChild(stateMessage('⚠️', '検索に失敗しました', error instanceof Error ? error.message : String(error)));
  }
}

// --------------------------------------------------------------- ライブラリ

export function renderLibrary(root: HTMLElement, navigate: Navigate): void {
  clear(root);
  root.appendChild(el('h1', { class: 'feed-head__title' }, 'ライブラリ'));

  const bookmarks = getBookmarks();
  const history = getHistory();

  const bookmarkSection = el('section', { style: 'margin-top:1.5rem' });
  bookmarkSection.appendChild(
    el('h2', { class: 'section-title' }, 'ブックマーク', el('span', { class: 'section-title__note' }, `${bookmarks.length} 件`)),
  );
  if (bookmarks.length === 0) {
    bookmarkSection.appendChild(stateMessage('🔖', 'ブックマークはまだありません', '論文詳細の「☆ ブックマーク」から追加できます。'));
  } else {
    for (const bookmark of bookmarks) {
      bookmarkSection.appendChild(
        el(
          'a',
          { class: 'list-item', href: `/papers/${bookmark.slug}`, 'data-link': '' },
          el(
            'div',
            { class: 'list-item__body' },
            el('div', { class: 'list-item__title' }, bookmark.headline || bookmark.title),
            el('p', { class: 'muted' }, `${bookmark.categoryName}｜追加 ${formatDate(bookmark.addedAt)}`),
          ),
        ),
      );
    }
  }
  root.appendChild(bookmarkSection);

  const historySection = el('section', { style: 'margin-top:2.5rem' });
  const historyHead = el('div', { class: 'row', style: 'justify-content:space-between' });
  historyHead.appendChild(
    el('h2', { class: 'section-title' }, '閲覧履歴', el('span', { class: 'section-title__note' }, `${history.length} 件`)),
  );
  if (history.length > 0) {
    historyHead.appendChild(
      el(
        'button',
        {
          class: 'button',
          type: 'button',
          onclick: () => {
            clearHistory();
            renderLibrary(root, navigate);
          },
        },
        '履歴を消去',
      ),
    );
  }
  historySection.appendChild(historyHead);
  if (history.length === 0) {
    historySection.appendChild(stateMessage('🕘', '閲覧履歴はまだありません', '読んだ論文がここに並びます（この端末内にのみ保存されます）。'));
  } else {
    for (const entry of history.slice(0, 50)) {
      historySection.appendChild(
        el(
          'a',
          { class: 'list-item', href: `/papers/${entry.slug}`, 'data-link': '' },
          el(
            'div',
            { class: 'list-item__body' },
            el('div', { class: 'list-item__title' }, entry.headline || entry.title),
            el('p', { class: 'muted' }, `${entry.categoryName}｜${formatDate(entry.viewedAt)}`),
          ),
        ),
      );
    }
  }
  root.appendChild(historySection);
}

// --------------------------------------------------------------- 設定

export async function renderSettings(root: HTMLElement, navigate: Navigate): Promise<void> {
  clear(root);
  const preferences = getPreferences();
  root.appendChild(el('h1', { class: 'feed-head__title' }, '設定'));

  const container = el('div', { class: 'stack', style: 'margin-top:1.5rem;max-width:44rem' });

  const displayCard = el('section', { class: 'card stack' });
  displayCard.appendChild(el('h2', { class: 'section-title' }, '表示'));
  displayCard.appendChild(
    segmented(
      '配色',
      [
        { value: 'light' as const, label: 'ライト' },
        { value: 'dark' as const, label: 'ダーク' },
      ],
      preferences.theme,
      (value) => {
        savePreferences({ theme: value });
        void renderSettings(root, navigate);
      },
    ),
  );
  container.appendChild(displayCard);

  const categoryCard = el('section', { class: 'card stack' });
  categoryCard.appendChild(
    el('h2', { class: 'section-title' }, '興味のある分野', el('span', { class: 'section-title__note' }, '選ばなければ全分野')),
  );
  const grid = el('div', { class: 'choice-grid' });
  categoryCard.appendChild(grid);
  container.appendChild(categoryCard);

  const accountCard = el('section', { class: 'card stack' });
  accountCard.appendChild(el('h2', { class: 'section-title' }, 'アカウント'));
  const accountBody = el('p', { class: 'muted' }, '読み込んでいます…');
  accountCard.appendChild(accountBody);
  const accountAction = el('div', { class: 'form-links' });
  accountCard.appendChild(accountAction);
  container.appendChild(accountCard);

  // 設定画面は配色などを変えるたびに自分を描き直すので、状態は覚えたものを使う
  // （loadMe が内部でキャッシュする）。器を先に置き、あとから埋める。
  void loadMe().then((me) => {
    clear(accountAction);
    const allContentFree = !me.monetizationEnabled;
    accountBody.textContent = me.authenticated
      ? me.tier === 'paid'
        ? `${me.email ?? ''} でログイン中です。スタンダードプランをご利用中です。`
        : allContentFree
          ? `${me.email ?? ''} でログイン中です。現在は全機能を無料でご利用いただけます。`
          : `${me.email ?? ''} でログイン中です。現在は無料でご利用中です。`
      : allContentFree
        ? '現在はアカウントなしで、過去を含む全論文・検索・引用グラフを無料でご利用いただけます。'
        : 'ログインすると、過去の論文・検索・引用グラフをご利用いただけます。';
    const go = el(
      'button',
      { class: 'button', type: 'button' },
      me.authenticated ? 'アカウントを管理' : allContentFree ? 'ログイン（任意）' : 'ログイン / 新規登録',
    );
    go.addEventListener('click', () => navigate(me.authenticated ? '/account' : '/login'));
    accountAction.appendChild(go);
  });

  const aboutCard = el('section', { class: 'card stack' });
  aboutCard.appendChild(el('h2', { class: 'section-title' }, 'PaperStock について'));
  aboutCard.appendChild(
    el(
      'p',
      { class: 'muted' },
      '合法的に全文へアクセスできるオープンアクセス論文のみを扱います。解説は AI が原論文をもとに作成し、自動検証を通したものです。人が1件ずつ内容を確認する工程はありません。解説はすべて「原論文に記載」「外部資料で確認」「考察・応用案」の3種類に分けて表示しています。医療・法律・投資に関する個別の助言は行いません。',
    ),
  );
  aboutCard.appendChild(
    el(
      'button',
      {
        class: 'button',
        type: 'button',
        onclick: () => {
          savePreferences({ onboarded: false });
          navigate('/onboarding');
        },
      },
      '初回設定をやり直す',
    ),
  );
  container.appendChild(aboutCard);

  root.appendChild(container);

  try {
    const categories = await loadCategories();
    const selected = new Set(getPreferences().categories);
    clear(grid);
    for (const category of categories) {
      grid.appendChild(
        el(
          'button',
          {
            type: 'button',
            class: 'choice',
            'aria-pressed': String(selected.has(category.code)),
            onclick: () => {
              const next = new Set(getPreferences().categories);
              if (next.has(category.code)) next.delete(category.code);
              else next.add(category.code);
              savePreferences({ categories: Array.from(next) });
              void renderSettings(root, navigate);
            },
          },
          category.name,
        ),
      );
    }
  } catch {
    clear(grid);
    grid.appendChild(el('p', { class: 'muted' }, '分野一覧を取得できませんでした。'));
  }
}

// --- 会員・契約 ---------------------------------------------------------

/**
 * 会員の状態は画面をまたいで何度も要る（施錠表示・設定画面・申し込み導線）。
 * 設定画面は配色を変えるたびに自分を描き直すため、そのたびに取りに行かないよう覚えておく。
 */
let meCache: MeResponse | null = null;
let meCacheAt = 0;
const ME_CACHE_MS = 30_000;

export function invalidateMe(): void {
  meCache = null;
  meCacheAt = 0;
}

export async function loadMe(force = false): Promise<MeResponse> {
  const fresh = Date.now() - meCacheAt < ME_CACHE_MS;
  if (!force && meCache !== null && fresh) return meCache;
  try {
    meCache = await fetchMe();
    meCacheAt = Date.now();
  } catch {
    // 取得できないときは「匿名」として扱う。ここで例外を投げると画面全体が落ちる。
    meCache = { authenticated: false, tier: 'anonymous', monetizationEnabled: true };
    meCacheAt = 0;
  }
  return meCache;
}

function formatPeriodEnd(epochSeconds: number | null | undefined): string {
  if (typeof epochSeconds !== 'number' || epochSeconds <= 0) return '不明';
  return formatDate(new Date(epochSeconds * 1000).toISOString().slice(0, 10));
}

function field(labelText: string, input: HTMLElement): HTMLElement {
  const wrap = el('label', { class: 'form-field' });
  wrap.appendChild(el('span', { class: 'form-field__label' }, labelText));
  wrap.appendChild(input);
  return wrap;
}

function textInput(attrs: Record<string, string>): HTMLInputElement {
  const input = el('input', { class: 'form-field__input', ...attrs }) as HTMLInputElement;
  return input;
}

function notice(kind: 'ok' | 'error', text: string): HTMLElement {
  return el('p', { class: `form-notice form-notice--${kind}`, role: kind === 'error' ? 'alert' : 'status' }, text);
}

/** ログインと登録。1つの画面で切り替える。 */
export async function renderLogin(
  root: HTMLElement,
  navigate: Navigate,
  params: URLSearchParams,
): Promise<void> {
  clear(root);
  const me = await loadMe(true);
  if (me.authenticated) {
    navigate('/account', { replace: true });
    return;
  }

  let mode: 'login' | 'signup' = params.get('mode') === 'signup' ? 'signup' : 'login';
  const container = el('section', { class: 'card form-card' });
  root.appendChild(el('h1', { class: 'feed-head__title' }, 'アカウント'));
  root.appendChild(container);

  const draw = (): void => {
    clear(container);
    container.appendChild(
      el('h2', { class: 'card__title' }, mode === 'login' ? 'ログイン' : '新規登録'),
    );
    container.appendChild(
      el(
        'p',
        { class: 'muted' },
        mode === 'login'
          ? 'ご登録のメールアドレスとパスワードを入力してください。'
          : 'メールアドレスとパスワードで登録します。確認メールをお送りします。',
      ),
    );

    const email = textInput({ type: 'email', name: 'email', autocomplete: 'email', required: 'true' });
    const password = textInput({
      type: 'password',
      name: 'password',
      autocomplete: mode === 'login' ? 'current-password' : 'new-password',
      required: 'true',
      minlength: '10',
    });
    const form = el('form', { class: 'form' });
    form.appendChild(field('メールアドレス', email));
    form.appendChild(field(mode === 'login' ? 'パスワード' : 'パスワード（10文字以上）', password));

    const submit = el('button', { class: 'button button--primary', type: 'submit' }, mode === 'login' ? 'ログイン' : '登録する');
    form.appendChild(submit);
    const messageHost = el('div');
    form.appendChild(messageHost);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      clear(messageHost);
      submit.setAttribute('disabled', 'true');
      const run = mode === 'login' ? login(email.value, password.value) : signup(email.value, password.value);
      void run
        .then(async (result) => {
          if (mode === 'login') {
            invalidateMe();
            await loadMe(true);
            navigate('/account');
            return;
          }
          clear(messageHost);
          messageHost.appendChild(
            notice('ok', (result as { message?: string }).message ?? '確認メールをお送りしました。'),
          );
        })
        .catch((error: unknown) => {
          clear(messageHost);
          messageHost.appendChild(
            notice('error', error instanceof Error ? error.message : '処理に失敗しました。'),
          );
        })
        .finally(() => submit.removeAttribute('disabled'));
    });
    container.appendChild(form);

    const switcher = el('div', { class: 'form-links' });
    const toggle = el('button', { class: 'button button--ghost', type: 'button' },
      mode === 'login' ? '新規登録はこちら' : 'ログインはこちら');
    toggle.addEventListener('click', () => {
      mode = mode === 'login' ? 'signup' : 'login';
      draw();
    });
    switcher.appendChild(toggle);

    if (mode === 'login') {
      const forgot = el('button', { class: 'button button--ghost', type: 'button' }, 'パスワードをお忘れの方');
      forgot.addEventListener('click', () => {
        clear(messageHost);
        if (email.value.trim().length === 0) {
          messageHost.appendChild(notice('error', 'メールアドレスを入力してから押してください。'));
          return;
        }
        void requestPasswordReset(email.value)
          .then((result) => {
            clear(messageHost);
            messageHost.appendChild(notice('ok', result.message));
          })
          .catch(() => {
            clear(messageHost);
            messageHost.appendChild(notice('error', '再設定のご案内を送れませんでした。'));
          });
      });
      switcher.appendChild(forgot);
    }
    container.appendChild(switcher);
  };
  draw();
}

/** 会員ページ。契約の状態と、申し込み・管理の導線。 */
export async function renderAccount(
  root: HTMLElement,
  navigate: Navigate,
  params: URLSearchParams,
): Promise<void> {
  clear(root);
  root.appendChild(el('h1', { class: 'feed-head__title' }, 'アカウント'));

  // Stripe からの戻り。**「完了しました」と断定しない。**
  // 決済のリダイレクトと Webhook の到着は順不同で、この時点では反映前のことがある。
  const checkout = params.get('checkout');
  if (checkout === 'success') {
    root.appendChild(
      notice('ok', 'お手続きを受け付けました。反映まで少しお時間をいただくことがあります。'),
    );
  } else if (checkout === 'cancel') {
    root.appendChild(notice('ok', 'お手続きを中止しました。料金は発生していません。'));
  }

  const card = el('section', { class: 'card' });
  root.appendChild(card);
  card.appendChild(el('p', { class: 'muted' }, '読み込んでいます…'));

  const me = await loadMe(true);
  clear(card);

  if (!me.authenticated) {
    card.appendChild(el('h2', { class: 'card__title' }, 'ログインしていません'));
    card.appendChild(
      el(
        'p',
        { class: 'muted' },
        me.monetizationEnabled
          ? 'ログインすると、過去の論文・検索・引用グラフをご利用いただけます。'
          : '現在はアカウントなしで、過去を含む全論文・検索・引用グラフを無料でご利用いただけます。',
      ),
    );
    const go = el(
      'button',
      { class: 'button button--primary', type: 'button' },
      me.monetizationEnabled ? 'ログイン / 新規登録' : '最新の論文を見る',
    );
    go.addEventListener('click', () => navigate(me.monetizationEnabled ? '/login' : '/'));
    card.appendChild(go);
    return;
  }

  card.appendChild(el('h2', { class: 'card__title' }, me.email ?? 'アカウント'));

  const statusText =
    !me.monetizationEnabled
      ? '現在は、過去を含む全論文・検索・引用グラフを無料でご利用いただけます。'
      : me.comped === true
      ? 'ご招待により、有料と同じ範囲をご利用いただけます。'
      : me.tier === 'paid'
        ? me.phase === 'grace'
          ? 'お支払いの確認中です。現在はご利用いただけます。'
          : 'スタンダードプランをご利用中です。'
        : me.phase === 'lapsed'
          ? 'ご契約は終了しています。当日の論文は引き続きお読みいただけます。'
          : '無料でご利用中です。当日の論文をお読みいただけます。';
  card.appendChild(el('p', {}, statusText));

  if (me.tier === 'paid' && me.comped !== true) {
    if (me.cancelAtPeriodEnd === true) {
      card.appendChild(
        el('p', { class: 'muted' }, `${formatPeriodEnd(me.currentPeriodEnd)} をもって終了予定です。次回のご請求はありません。`),
      );
    } else if (typeof me.currentPeriodEnd === 'number') {
      card.appendChild(el('p', { class: 'muted' }, `次回のご請求日: ${formatPeriodEnd(me.currentPeriodEnd)}`));
    }
  }

  if (me.emailVerified === false) {
    card.appendChild(
      notice(
        'error',
        me.monetizationEnabled
          ? 'メールアドレスの確認が済んでいません。お申し込みの前に、確認メールのリンクを開いてください。'
          : 'メールアドレスの確認が済んでいません。アカウント機能を使うには、確認メールのリンクを開いてください。',
      ),
    );
  }

  const actions = el('div', { class: 'form-links' });
  const messageHost = el('div');

  const billing = me.monetizationEnabled
    ? await fetchBillingStatus().catch(() => ({ available: false, missingConfig: [] as string[] }))
    : { available: false, missingConfig: [] as string[] };

  if (me.tier === 'paid' && me.comped !== true) {
    const portal = el('button', { class: 'button button--primary', type: 'button' }, 'お支払い・解約の管理');
    portal.addEventListener('click', () => {
      portal.setAttribute('disabled', 'true');
      void openBillingPortal()
        .then((result) => {
          // CSP の form-action 'self' があるため、form POST ではなくトップレベル遷移にする。
          window.location.href = result.url;
        })
        .catch((error: unknown) => {
          clear(messageHost);
          messageHost.appendChild(notice('error', error instanceof Error ? error.message : '開けませんでした。'));
          portal.removeAttribute('disabled');
        });
    });
    actions.appendChild(portal);
  } else if (me.comped !== true && me.monetizationEnabled) {
    const subscribe = el('button', { class: 'button button--primary', type: 'button' }, '月額500円（税込）で申し込む');
    if (!billing.available) {
      subscribe.setAttribute('disabled', 'true');
      subscribe.textContent = 'お申し込みの準備中です';
    }
    subscribe.addEventListener('click', () => {
      subscribe.setAttribute('disabled', 'true');
      void startCheckout()
        .then((result) => {
          window.location.href = result.url;
        })
        .catch((error: unknown) => {
          clear(messageHost);
          messageHost.appendChild(notice('error', error instanceof Error ? error.message : '開けませんでした。'));
          subscribe.removeAttribute('disabled');
        });
    });
    actions.appendChild(subscribe);
  } else if (!me.monetizationEnabled) {
    actions.appendChild(el('p', { class: 'muted' }, '有料プランの新規申し込みは停止中です。'));
  }

  const signOut = el('button', { class: 'button button--ghost', type: 'button' }, 'ログアウト');
  signOut.addEventListener('click', () => {
    void logout()
      .then(() => {
        invalidateMe();
        navigate('/');
      })
      .catch(() => {
        clear(messageHost);
        messageHost.appendChild(notice('error', 'ログアウトできませんでした。'));
      });
  });
  actions.appendChild(signOut);
  card.appendChild(actions);
  card.appendChild(messageHost);

  // --- 端末をまたぐ同期 ---
  const syncCard = el('section', { class: 'card' });
  syncCard.appendChild(el('h2', { class: 'card__title' }, '端末をまたぐ同期'));
  syncCard.appendChild(
    el(
      'p',
      { class: 'muted' },
      'ブックマーク・閲覧履歴・読み方の設定を、この端末とアカウントのあいだで突き合わせます。配色は端末ごとの設定なので同期しません。',
    ),
  );
  const syncMessage = el('div');
  const syncButton = el('button', { class: 'button', type: 'button' }, 'いま同期する');
  syncButton.addEventListener('click', () => {
    syncButton.setAttribute('disabled', 'true');
    clear(syncMessage);
    void syncWithAccount()
      .then((result) => {
        clear(syncMessage);
        syncMessage.appendChild(notice(result.ok ? 'ok' : 'error', result.message));
      })
      .finally(() => syncButton.removeAttribute('disabled'));
  });
  syncCard.appendChild(syncButton);
  syncCard.appendChild(syncMessage);
  root.appendChild(syncCard);

  // --- 退会 ---
  const dangerCard = el('section', { class: 'card' });
  dangerCard.appendChild(el('h2', { class: 'card__title' }, '退会'));
  dangerCard.appendChild(
    el(
      'p',
      { class: 'muted' },
      'アカウントとブックマーク・閲覧履歴を削除します。ご契約がある場合は解約してから削除します。**元に戻せません。**',
    ),
  );
  const dangerMessage = el('div');
  const confirmLabel = el('label', { class: 'form-field' });
  const confirmBox = el('input', { type: 'checkbox' }) as HTMLInputElement;
  confirmLabel.appendChild(confirmBox);
  confirmLabel.appendChild(el('span', { class: 'form-field__label' }, '削除されることを理解しました'));
  dangerCard.appendChild(confirmLabel);
  const deleteButton = el('button', { class: 'button', type: 'button' }, 'アカウントを削除する');
  deleteButton.addEventListener('click', () => {
    clear(dangerMessage);
    if (!confirmBox.checked) {
      dangerMessage.appendChild(notice('error', '確認の印を付けてください。'));
      return;
    }
    deleteButton.setAttribute('disabled', 'true');
    void deleteAccount()
      .then(() => {
        invalidateMe();
        navigate('/');
      })
      .catch((error: unknown) => {
        clear(dangerMessage);
        dangerMessage.appendChild(
          notice('error', error instanceof Error ? error.message : '削除できませんでした。'),
        );
        deleteButton.removeAttribute('disabled');
      });
  });
  dangerCard.appendChild(deleteButton);
  dangerCard.appendChild(dangerMessage);
  root.appendChild(dangerCard);
}

/** 確認メールのリンクから来る画面。 */
export async function renderVerifyEmail(
  root: HTMLElement,
  navigate: Navigate,
  params: URLSearchParams,
): Promise<void> {
  clear(root);
  root.appendChild(el('h1', { class: 'feed-head__title' }, 'メールアドレスの確認'));
  const card = el('section', { class: 'card' });
  root.appendChild(card);

  const token = params.get('token');
  if (token === null || token.length === 0) {
    card.appendChild(notice('error', '確認用のリンクが正しくありません。'));
    return;
  }
  card.appendChild(el('p', { class: 'muted' }, '確認しています…'));
  try {
    const result = await verifyEmail(token);
    invalidateMe();
    clear(card);
    card.appendChild(notice('ok', result.message));
    const go = el('button', { class: 'button button--primary', type: 'button' }, 'アカウントへ');
    go.addEventListener('click', () => navigate('/account'));
    card.appendChild(go);
  } catch (error) {
    clear(card);
    card.appendChild(notice('error', error instanceof Error ? error.message : '確認できませんでした。'));
  }
}

/** 再設定メールのリンクから来る画面。 */
export async function renderResetPassword(
  root: HTMLElement,
  navigate: Navigate,
  params: URLSearchParams,
): Promise<void> {
  clear(root);
  root.appendChild(el('h1', { class: 'feed-head__title' }, 'パスワードの再設定'));
  const card = el('section', { class: 'card form-card' });
  root.appendChild(card);

  const token = params.get('token');
  if (token === null || token.length === 0) {
    card.appendChild(notice('error', '再設定用のリンクが正しくありません。'));
    return;
  }

  const password = textInput({ type: 'password', name: 'password', autocomplete: 'new-password', required: 'true', minlength: '10' });
  const form = el('form', { class: 'form' });
  form.appendChild(field('新しいパスワード（10文字以上）', password));
  const submit = el('button', { class: 'button button--primary', type: 'submit' }, '変更する');
  form.appendChild(submit);
  const messageHost = el('div');
  form.appendChild(messageHost);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    submit.setAttribute('disabled', 'true');
    void resetPassword(token, password.value)
      .then((result) => {
        invalidateMe();
        clear(messageHost);
        messageHost.appendChild(notice('ok', result.message));
        navigate('/account');
      })
      .catch((error: unknown) => {
        clear(messageHost);
        messageHost.appendChild(notice('error', error instanceof Error ? error.message : '変更できませんでした。'));
        submit.removeAttribute('disabled');
      });
  });
  card.appendChild(form);
}

/**
 * 端末内の内容を会員と同期する。
 *
 * 端末側とサーバ側で新しい方を採る。ブックマークの「外した」は墓標として送るので、
 * 2つの端末をまたいでも外したものが復活しない。
 * **配色は送らない**（端末ごとの見た目で、初期表示のちらつき防止が localStorage を直接読むため）。
 */
export async function syncWithAccount(): Promise<{ ok: boolean; message: string }> {
  const me = await loadMe();
  if (!me.authenticated) return { ok: false, message: 'ログインすると端末をまたいで同期できます。' };

  const preferences = getPreferences();
  try {
    const result = await pushSync({
      bookmarks: getBookmarksForSync().map((b) => ({
        paperId: b.paperId,
        slug: b.slug,
        title: b.title,
        headline: b.headline,
        categoryName: b.categoryName,
        addedAt: Date.parse(b.addedAt) || Date.now(),
        deletedAt: b.deletedAt === undefined ? null : Date.parse(b.deletedAt) || Date.now(),
      })),
      history: getHistory().map((h) => ({
        paperId: h.paperId,
        slug: h.slug,
        title: h.title,
        headline: h.headline,
        categoryName: h.categoryName,
        viewedAt: Date.parse(h.viewedAt) || Date.now(),
      })),
      preferences: {
        categories: preferences.categories,
        level: preferences.level,
        medium: preferences.medium,
        graphDirection: preferences.graphDirection,
      },
    });

    replaceBookmarks(
      result.bookmarks.map((b) => ({
        paperId: b.paperId,
        slug: b.slug,
        title: b.title,
        headline: b.headline ?? '',
        categoryName: b.categoryName ?? '',
        addedAt: new Date((b.addedAt ?? 0) * 1000).toISOString(),
        ...(b.deletedAt ? { deletedAt: new Date(b.deletedAt * 1000).toISOString() } : {}),
      })),
    );
    replaceHistory(
      result.history.map((h) => ({
        paperId: h.paperId,
        slug: h.slug,
        title: h.title,
        headline: h.headline ?? '',
        categoryName: h.categoryName ?? '',
        viewedAt: new Date((h.viewedAt ?? 0) * 1000).toISOString(),
      })),
    );
    if (result.preferences) {
      savePreferences({
        categories: result.preferences.categories,
        graphDirection: result.preferences.graphDirection as 'all' | 'references' | 'cited_by',
      });
    }
    return { ok: true, message: '同期しました。' };
  } catch (error) {
    return { ok: false, message: error instanceof Error ? error.message : '同期できませんでした。' };
  }
}


// --- 法令表記 ---------------------------------------------------------------
// **購入の導線から辿れる場所に無いと意味が無い**ので、アプリ内に置いている。
// ホームページ（WebMado）には置けない（terms / tokushoho は予約語でテナントが作れない）。

export async function renderLegal(
  root: HTMLElement,
  _navigate: Navigate,
  kind: 'tokushoho' | 'terms' | 'privacy',
): Promise<void> {
  clear(root);
  const title =
    kind === 'tokushoho'
      ? '特定商取引法に基づく表記'
      : kind === 'terms'
        ? '利用規約'
        : 'プライバシーポリシー（会員機能）';
  root.appendChild(el('h1', { class: 'feed-head__title' }, title));
  const card = el('section', { class: 'card' });
  card.appendChild(el('p', { class: 'muted' }, '読み込んでいます…'));
  root.appendChild(card);

  let legal;
  try {
    legal = await fetchLegal();
  } catch {
    clear(card);
    card.appendChild(stateMessage('⚠️', '表示できませんでした', '時間をおいてお試しください。'));
    return;
  }
  clear(card);

  if (kind === 'tokushoho') {
    if (!legal.tokushoho.complete) {
      // 「準備中」と正直に出す。未記入の項目を空欄で並べると、
      // 記載義務を満たしているように見えてしまう。
      card.appendChild(
        el('p', { class: 'form-notice form-notice--error' },
          'この表記はまだ準備中です。有料のお申し込みは受け付けていません。'),
      );
    }
    const table = el('div', { class: 'legal-table' });
    for (const row of legal.tokushoho.rows) {
      const item = el('div', { class: 'legal-table__row' });
      item.appendChild(el('div', { class: 'legal-table__label' }, row.label));
      item.appendChild(el('div', { class: 'legal-table__value' }, row.value));
      table.appendChild(item);
    }
    card.appendChild(table);
    return;
  }

  const doc = kind === 'terms' ? legal.terms : legal.privacy;
  const pending =
    kind === 'terms'
      ? 'この規約はまだ準備中です。有料のお申し込みは受け付けていません。'
      : 'この方針はまだ準備中です。会員登録は受け付けていません。';
  if (doc.updatedAt) {
    card.appendChild(el('p', { class: 'muted' }, `${formatDate(doc.updatedAt)} 版`));
  } else {
    card.appendChild(el('p', { class: 'form-notice form-notice--error' }, pending));
  }
  if (kind === 'privacy') {
    card.appendChild(
      el('p', { class: 'muted' },
        'ホームページに掲載しているプライバシーポリシーは、お問い合わせフォームを対象としたものです。会員機能（アカウント・ブックマーク・閲覧履歴・お支払い）については、こちらをご覧ください。'),
    );
  }
  for (const section of doc.sections) {
    card.appendChild(el('h2', { class: 'card__title' }, section.heading));
    for (const line of section.body) {
      card.appendChild(el('p', {}, line));
    }
  }
}

/** ログインせず依頼できる。受付と採用・記事公開を区別する。 */
export async function renderPaperRequests(root: HTMLElement): Promise<void> {
  clear(root);
  document.title = 'PaperStock｜論文の解説をリクエスト';
  root.appendChild(el('h1', { class: 'card__title' }, '論文の解説をリクエスト'));
  const container = el('section', { class: 'card form-card' },
    el('p', {}, '読んでみたい論文と、特に知りたい点を教えてください。対応可能な論文は、AIによる日本語解説を作成し、PaperStockで公開します。'),
    el('p', { class: 'muted' }, '無料でリクエストできます。すべての依頼への対応や公開時期をお約束するものではありません。個別の納品やメール通知は行いません。'),
  );
  root.appendChild(container);
  try {
    if (!(await fetchPaperRequestAvailability()).available) {
      container.appendChild(notice('error', 'ただいま受付を準備中です。時間をおいてお試しください。'));
      return;
    }
  } catch {
    container.appendChild(notice('error', '受付状況を取得できませんでした。時間をおいてページを開き直してください。'));
    return;
  }
  if (!root.contains(container)) return;
  const identifier = textInput({ type: 'text', name: 'identifier', required: 'true', maxlength: String(requestConfig.maxIdentifierChars), placeholder: 'https://arxiv.org/abs/… または 10.…/…', autocomplete: 'off' });
  const question = el('textarea', { class: 'form-field__input', name: 'question', required: true, maxlength: requestConfig.maxQuestionChars, rows: 5, placeholder: '例：何と比較して、どの条件で効果があったのかを知りたい' });
  const consent = el('input', { type: 'checkbox', name: 'consent', required: true });
  const website = textInput({ type: 'text', name: 'website', tabindex: '-1', autocomplete: 'off' });
  const form = el('form', { class: 'form' },
    field('論文URLまたはDOI（必須）', identifier),
    el('p', { class: 'muted' }, 'PDFの添付は受け付けていません。原文を取得できない論文は対応できない場合があります。'),
    field(`特に知りたい点（必須・${requestConfig.maxQuestionChars}文字以内）`, question),
    el('p', { class: 'muted', id: 'request-privacy-note' }, '氏名・メールアドレス・電話番号・秘密情報は入力しないでください。入力内容は運営のみが閲覧できる受付台帳とGitHubで、対応の検討・管理に利用します。依頼文は記事に掲載しません。'),
    el('div', { hidden: true, 'aria-hidden': 'true' }, field('この欄は空欄にしてください', website)),
    el('label', {}, consent, ' AIによる解説であること、採用・納期が未確約であること、完成した解説を公開することを確認しました。'),
  );
  question.setAttribute('aria-describedby', 'request-privacy-note');
  const submit = el('button', { class: 'button button--primary', type: 'submit' }, 'リクエストを送る');
  const messageHost = el('div');
  form.append(submit, messageHost);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submit.disabled) return;
    clear(messageHost);
    submit.disabled = true;
    submit.textContent = '送信中…';
    void submitPaperRequest({ identifier: identifier.value, question: question.value, consent: consent.checked, website: website.value })
      .then((receipt) => {
        clear(container);
        container.append(notice('ok', receipt.message), el('p', { class: 'muted' }, `受付番号：${receipt.id}`),
          el('a', { class: 'button', href: '/', 'data-link': true }, '今日の論文を見る'));
      })
      .catch((error: unknown) => {
        messageHost.appendChild(notice('error', error instanceof ApiError ? error.message : '通信に失敗しました。入力内容は残っています。再度送信してください。'));
        submit.disabled = false;
        submit.textContent = 'リクエストを送る';
      });
  });
  container.appendChild(form);
}
